import { useAurigaContext } from '@context/AurigaContext';
import { useDecklinesContext } from '@context/DecklinesContext.tsx';
import { useHighlightingContext } from '@context/HighlightingContext';
import { usePageContext } from '@context/PageContext.tsx';
import { parseIdFromKey } from '@utils/deckHelpers';
import { changeMultipleEntryFields } from '@utils/deckManagementEntityHelpers';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { DecklineRowData } from 'types/Page.types';
import { PageSidePanelDropDown } from 'types/PageSidePanel.types';
/**
 * Custom hook for managing subtotal functionality including range validation and calculation
 * @param pageRowData - Array of page row data for validation
 * @returns Object containing subtotal state and handler functions
 */
const useSubtotalHelper = (pageRowData?: DecklineRowData[]) => {
  const { toastNotification } = useAurigaContext();
  const { applyHighlighting, clearSubtotalHighlighting } = useHighlightingContext();
  const { setPageRowData } = usePageContext();
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);
  const [highlightedRange, setHighlightedRange] = useState<{ start: number; end: number } | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pageRowDataRef = useRef(pageRowData);
  const validationTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const { selectedDeckline, saveSubtotal, setDecklineType } = useDecklinesContext();

  // Update ref when pageRowData changes
  useEffect(() => {
    pageRowDataRef.current = pageRowData;
  }, [pageRowData]);

  // Clear highlightedRange when selectedDeckline changes (user switched rows)
  useEffect(() => {
    setHighlightedRange(null);
  }, [selectedDeckline?.metadata?.internalId]);

  /**
   * Creates deckline items input for subtotal functionality
   * @returns Array of EnhancedDeckline containing subtotal deckline and updated child decklines
   */
  const updatedSubotalRows = useCallback((): Array<EnhancedDeckline> => {
    if (!highlightedRange || !pageRowDataRef.current || !selectedDeckline) return [];

    const { start, end } = highlightedRange;
    const enhancedSubtotalDecklineList: Array<EnhancedDeckline> = [];

    // Create subtotal formula from kpi_ids
    const kpiIds = pageRowDataRef.current
      .filter(
        (row) =>
          row?.rowNumber &&
          Number(row.rowNumber) >= start &&
          Number(row.rowNumber) <= end &&
          row?.rawDecklineData?.kpi_id,
      )
      .map((row) => row!.rawDecklineData!.kpi_id!);

    // Get the current kpi_name value from pageRowData (includes any user edits)
    let currentKpiName = selectedDeckline.kpi_name;
    if (pageRowDataRef.current && selectedDeckline.sk) {
      const currentRow = pageRowDataRef.current.find((row) => row.rawDecklineData?.sk === selectedDeckline.sk);
      if (currentRow) {
        currentKpiName = currentRow.kpi_name || selectedDeckline.kpi_name;
      }
    }

    // Create subtotal deckline
    const newDecklineWithSubtotal = changeMultipleEntryFields(selectedDeckline, {
      deckline_id: parseIdFromKey(selectedDeckline.sk!),
      subtotal_formula: kpiIds.join('+'),
      kpi_name: currentKpiName,
      blankline_type: null,
    });
    enhancedSubtotalDecklineList.push(newDecklineWithSubtotal);

    const subtotalId = newDecklineWithSubtotal.deckline_id;

    // Process all rows to handle subtotal_parent updates
    pageRowDataRef.current.forEach((row) => {
      const rowData = row?.rawDecklineData;
      if (!rowData || !row?.rowNumber) return;

      const rowNumber = Number(row.rowNumber);
      const isInNewRange = rowNumber >= start && rowNumber <= end;
      const currentlyHasSubtotalParent = rowData.subtotal_parent === subtotalId;

      // Update row if it needs subtotal_parent changes
      if (isInNewRange || currentlyHasSubtotalParent) {
        enhancedSubtotalDecklineList.push(
          changeMultipleEntryFields(rowData, {
            deckline_id: parseIdFromKey(rowData.sk!),
            subtotal_parent: isInNewRange ? subtotalId : null,
          }),
        );
      }
    });

    return enhancedSubtotalDecklineList;
  }, [highlightedRange, selectedDeckline]);

  /**
   * Applies subtotal range highlighting to rows within a specified range.
   * @param start - The starting row number (inclusive)
   * @param end - The ending row number (inclusive)
   * @param rowData - Array of DecklineRowData to apply highlighting to
   */
  const applySubtotalRangeHighlighting = useCallback(
    (start: number, end: number, rowData: DecklineRowData[]) => {
      applyHighlighting(
        rowData,
        (row) => {
          const rowNumber = Number(row.rowNumber);
          return rowNumber >= start && rowNumber <= end && !Number.isNaN(rowNumber);
        },
        'is_subtotal_highlighted',
      );
    },
    [applyHighlighting],
  );

  // Re-apply highlighting when pageRowData is regenerated (after save)
  useEffect(() => {
    if (highlightedRange && pageRowData && pageRowData.length > 0) {
      applySubtotalRangeHighlighting(highlightedRange.start, highlightedRange.end, pageRowData);
    }
  }, [pageRowData, highlightedRange, applySubtotalRangeHighlighting]);

  /**
   * Validates the start and end row inputs and applies highlighting
   */
  const validateAndSetRange = useCallback(
    ({ start, end }: { start: string; end: string }) => {
      if (!start.trim() || !end.trim()) {
        if (pageRowDataRef.current) {
          clearSubtotalHighlighting(pageRowDataRef.current);
        }
        return;
      }

      const startNum = parseInt(start, 10);
      const endNum = parseInt(end, 10);

      if (Number.isNaN(startNum) || Number.isNaN(endNum)) {
        toastNotification({
          message: 'Row numbers must be valid numbers',
          visible: true,
          type: 'warning',
        });
        if (pageRowDataRef.current) {
          clearSubtotalHighlighting(pageRowDataRef.current);
        }
        return;
      }

      if (startNum < 1 || endNum < 1) {
        toastNotification({
          message: 'Row numbers must be positive',
          visible: true,
          type: 'warning',
        });
        if (pageRowDataRef.current) {
          clearSubtotalHighlighting(pageRowDataRef.current);
        }
        return;
      }

      if (startNum > endNum) {
        toastNotification({
          message: 'Start row cannot be greater than end row',
          visible: true,
          type: 'warning',
        });
        if (pageRowDataRef.current) {
          clearSubtotalHighlighting(pageRowDataRef.current);
        }
        return;
      }

      // Check if end index exceeds current serial number
      if (selectedDeckline?.serial_no) {
        const currentSerialNo = parseFloat(selectedDeckline.serial_no);
        if (endNum > currentSerialNo) {
          toastNotification({
            message: `End row cannot be greater than current serial number (${currentSerialNo})`,
            visible: true,
            type: 'warning',
          });
          if (pageRowDataRef.current) {
            clearSubtotalHighlighting(pageRowDataRef.current);
          }
          return;
        }
      }

      if (pageRowDataRef.current) {
        applySubtotalRangeHighlighting(startNum, endNum, pageRowDataRef.current);
        setHighlightedRange({ start: startNum, end: endNum });
      }
    },
    [applySubtotalRangeHighlighting, clearSubtotalHighlighting, toastNotification, selectedDeckline],
  );

  /**
   * Calculates subtotal and clears highlighting
   */
  const calculateSubtotal = useCallback(() => {
    const currentStart = startInputRef.current?.value || '';
    const currentEnd = endInputRef.current?.value || '';

    if (!currentStart.trim() || !currentEnd.trim()) {
      toastNotification({
        message: 'Please enter both start and end row numbers',
        visible: true,
        type: 'warning',
      });
      return;
    }

    const finalUpdatedSubtotalDecklines = updatedSubotalRows();

    saveSubtotal(finalUpdatedSubtotalDecklines, () => {
      // Enable editing for the selected row's kpi_name
      if (pageRowDataRef.current && selectedDeckline?.serial_no) {
        const updatedPageRowData = pageRowDataRef.current.map((row) => {
          if (Number(row.rawDecklineData.serial_no) === Number(selectedDeckline.serial_no)) {
            return { ...row, is_kpi_name_editable: true };
          }
          return row;
        });
        setPageRowData(updatedPageRowData);
      }

      // Show success toast only after successful save
      toastNotification({
        message: 'Subtotal row created , save changes by clicking on Save button',
        visible: true,
        type: 'success',
      });
    });
  }, [updatedSubotalRows, toastNotification, saveSubtotal, selectedDeckline?.serial_no, setPageRowData]);

  /**
   * Debounced validation function
   */
  const debouncedValidation = useCallback(
    ({ start, end }: { start: string; end: string }) => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      validationTimeoutRef.current = setTimeout(() => {
        validateAndSetRange({ start, end });
      }, 500); // Wait 500ms after user stops typing
    },
    [validateAndSetRange],
  );

  /**
   * Handles start row input change with debounced validation
   */
  const handleStartRowChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const start = event.target.value;
      const end = endInputRef.current?.value || '';
      debouncedValidation({ start, end });
    },
    [debouncedValidation],
  );

  /**
   * Handles end row input change with debounced validation
   */
  const handleEndRowChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const start = startInputRef.current?.value || '';
      const end = event.target.value;
      debouncedValidation({ start, end });
    },
    [debouncedValidation],
  );

  /**
   * Checks for unsaved subtotal changes and shows dialog if needed.
   */
  const checkUnsavedChanges = useCallback(() => {
    if (highlightedRange) {
      setShowUnsavedDialog(true);
      return true;
    }
    return false;
  }, [highlightedRange]);

  /**
   * Discards unsaved subtotal changes
   */
  const discardUnsavedChanges = useCallback(() => {
    if (pageRowDataRef.current) {
      clearSubtotalHighlighting(pageRowDataRef.current);
    }
    if (startInputRef.current) startInputRef.current.value = '';
    if (endInputRef.current) endInputRef.current.value = '';
    setHighlightedRange(null);
    setShowUnsavedDialog(false);
    setDecklineType(PageSidePanelDropDown.BASE_KPI);
  }, [clearSubtotalHighlighting, setDecklineType]);

  return {
    calculateSubtotal,
    handleStartRowChange,
    handleEndRowChange,
    startInputRef,
    endInputRef,
    highlightedRange,
    showUnsavedDialog,
    setShowUnsavedDialog,
    checkUnsavedChanges,
    discardUnsavedChanges,
    applySubtotalRangeHighlighting,
  };
};

export default useSubtotalHelper;
